import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import { LoaderCircle, Save, Settings as SettingsIcon, Globe, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];
const WRITE_ROLES = ["otel_owner", "otel_manager", "admin", "super_admin"];

const Field = ({ label, hint, children }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</label>
        {children}
        {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
    </div>
);

const INP = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

export default function HotelSettingsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [hotel, setHotel] = useState(null);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const canWrite = user && WRITE_ROLES.includes(user.role);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/hotel/settings");
            setHotel(r.data);
            setForm({
                description: r.data.description || "",
                short_description: r.data.short_description || "",
                phone: r.data.phone || "",
                email: r.data.email || "",
                address: r.data.address || "",
                website: r.data.website || "",
                facebook_url: r.data.facebook_url || "",
                instagram_url: r.data.instagram_url || "",
                check_in_time: r.data.check_in_time || "14:00",
                check_out_time: r.data.check_out_time || "12:00",
                hero_title: r.data.hero_title || "",
                hero_subtitle: r.data.hero_subtitle || "",
                site_slug: r.data.site_slug || "",
                site_published: r.data.site_published === true,
            });
        } finally { setLoading(false); }
    }, []);

    /* eslint-disable */
    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate("/h/login");
        if (!HOTEL_ROLES.includes(user.role)) return navigate("/admin");
        load();
    }, [user, authLoading, navigate, load]);
    /* eslint-enable */

    const submit = async (e) => {
        e.preventDefault();
        if (!canWrite) return;
        setSaving(true);
        try {
            await api.put("/hotel/settings", form);
            toast.success("Ayarlar kaydedildi");
            load();
        } catch (e2) {
            toast.error(e2.response?.data?.detail || "Kaydedilemedi");
        } finally { setSaving(false); }
    };

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    if (loading || !hotel) {
        return <HotelLayout title="Otel Ayarları"><div className="p-12 flex items-center justify-center"><LoaderCircle className="h-8 w-8 text-emerald-500 animate-spin" /></div></HotelLayout>;
    }

    return (
        <HotelLayout title="Otel Ayarları" hotelName={user?.full_name?.split(" ")[0]}>
            <form onSubmit={submit} className="max-w-4xl space-y-5">
                {/* Identity */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
                    <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><SettingsIcon className="h-4 w-4 text-emerald-500" /> Otel Kimliği</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Otel Adı"><div className="px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm font-semibold text-slate-700 dark:text-slate-200">{hotel.name}</div></Field>
                        <Field label="Şehir / Destinasyon"><div className="px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm font-semibold text-slate-700 dark:text-slate-200">{hotel.destination_name || "—"}</div></Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Field label="Kısa Açıklama (3-5 cümle)">
                            <textarea data-testid="set-short-desc" rows={3} value={form.short_description} onChange={(e) => upd("short_description", e.target.value)} disabled={!canWrite} className={INP} />
                        </Field>
                        <Field label="Detaylı Açıklama">
                            <textarea data-testid="set-desc" rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)} disabled={!canWrite} className={INP} />
                        </Field>
                    </div>
                </div>

                {/* Contact */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
                    <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><Phone className="h-4 w-4 text-sky-500" /> İletişim</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Telefon"><input data-testid="set-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} disabled={!canWrite} className={INP} /></Field>
                        <Field label="E-posta"><input data-testid="set-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} disabled={!canWrite} className={INP} /></Field>
                        <Field label="Adres" hint="Tam açık adres"><input data-testid="set-address" value={form.address} onChange={(e) => upd("address", e.target.value)} disabled={!canWrite} className={INP} /></Field>
                        <Field label="Web Sitesi"><input data-testid="set-website" type="url" value={form.website} onChange={(e) => upd("website", e.target.value)} disabled={!canWrite} placeholder="https://..." className={INP} /></Field>
                    </div>
                </div>

                {/* Times */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
                    <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-500" /> Check-in / Check-out</h3>
                    <div className="grid grid-cols-2 gap-4 max-w-md">
                        <Field label="Check-in"><input data-testid="set-ci" type="time" value={form.check_in_time} onChange={(e) => upd("check_in_time", e.target.value)} disabled={!canWrite} className={INP} /></Field>
                        <Field label="Check-out"><input data-testid="set-co" type="time" value={form.check_out_time} onChange={(e) => upd("check_out_time", e.target.value)} disabled={!canWrite} className={INP} /></Field>
                    </div>
                </div>

                {/* Social */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5">
                    <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><Globe className="h-4 w-4 text-purple-500" /> Sosyal Medya</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Facebook URL"><input data-testid="set-fb" value={form.facebook_url} onChange={(e) => upd("facebook_url", e.target.value)} disabled={!canWrite} className={INP} /></Field>
                        <Field label="Instagram URL"><input data-testid="set-ig" value={form.instagram_url} onChange={(e) => upd("instagram_url", e.target.value)} disabled={!canWrite} className={INP} /></Field>
                    </div>
                </div>

                {/* Microsite (Faz 3 hazırlığı) */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 rounded-2xl border border-purple-200 dark:border-purple-900 p-5">
                    <h3 className="font-bold text-slate-900 dark:text-white mb-1 flex items-center gap-2">🌐 Otel Mikrositi (Yakında)</h3>
                    <p className="text-[11px] text-slate-600 dark:text-slate-400 mb-4">Faz 3&apos;te her otele otomatik subdomain (otelaadi.msonlinetravel.com) atanacak. Şimdiden temel bilgileri girebilirsiniz.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Site Slug (URL adı)" hint="ör: mojemore → mojemore.msonlinetravel.com">
                            <input data-testid="set-slug" value={form.site_slug} onChange={(e) => upd("site_slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} disabled={!canWrite} placeholder="mojemore" className={INP} />
                        </Field>
                        <Field label="Hero Başlık">
                            <input data-testid="set-hero-title" value={form.hero_title} onChange={(e) => upd("hero_title", e.target.value)} disabled={!canWrite} placeholder="Hoş geldiniz, biz Moje More..." className={INP} />
                        </Field>
                        <Field label="Hero Alt Başlık">
                            <input data-testid="set-hero-sub" value={form.hero_subtitle} onChange={(e) => upd("hero_subtitle", e.target.value)} disabled={!canWrite} placeholder="Tekirdağ kıyısının saklı cenneti" className={INP} />
                        </Field>
                        <Field label="Yayında mı?">
                            <label className="flex items-center gap-2 mt-2">
                                <input data-testid="set-published" type="checkbox" checked={form.site_published} onChange={(e) => upd("site_published", e.target.checked)} disabled={!canWrite} className="h-4 w-4 rounded text-purple-500" />
                                <span className="text-sm text-slate-700 dark:text-slate-300">Microsite yayında olsun</span>
                            </label>
                        </Field>
                    </div>
                </div>

                {canWrite && (
                    <div className="sticky bottom-0 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 flex justify-end shadow-lg">
                        <button data-testid="set-save" type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-sm">
                            <Save className="h-4 w-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
                        </button>
                    </div>
                )}
                {!canWrite && (
                    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-300" data-testid="set-readonly-banner">
                        🔒 Bu sayfayı görüntüleyebilirsiniz ama düzenleme yetkiniz yok. Yönetici veya Otel Sahibi rolüne sahip kullanıcı düzenleyebilir.
                    </div>
                )}
            </form>
        </HotelLayout>
    );
}
